import { useMemo } from "react";
import { useDispatch } from "react-redux";

import { useQueries } from "@tanstack/react-query";

import { apiThatThrows } from "containers/Request";
import { useAppsByIds } from "hooks/applications";
import { TiVoAppProvider } from "hooks/tivoApps";
import { unique } from "utils/array";

// it's limited type with fields needed for manifest list view

type VewdApp = {
  id: string;
  name: string;
  icon: string | null;
  slug: string;
};

type ManifestApp = {
  id: string;
  name: string;
  icon: string;
  path: string;
  slug?: string;
};

const providerToApp = (app: TiVoAppProvider): ManifestApp => ({
  id: app.providerId,
  name: app.label,
  icon: app.assets?.prod?.primaryBrandingLogoSquare?.url ?? "",
  path: `/tivoapps/details/${app.id}/`,
});

const legacyAppToListItem = (app: VewdApp): ManifestApp => ({
  id: app.id,
  name: app.name,
  icon: app.icon ?? "",
  path: `/applications/moderation/${app.id}/details/`,
  slug: app.slug,
});

export function useAppsWithProviders(
  appIds: string[],
  enabled: boolean
): { isPending: boolean; apps: ManifestApp[] } {
  const dispatch = useDispatch();
  // Temporary workaround blocked by TAP-419, this endpoint will support multiple provider IDs.
  const tivoApps = useQueries({
    queries: unique(appIds).map((appId) => ({
      queryKey: ["tivoApps", appId],
      queryFn: () => {
        return dispatch(
          apiThatThrows.getTiVoProviders.action({
            queryParams: { providerId: appId, limit: 1, page: 1 },
          })
        );
      },
      enabled,
      refetchOnMount: false,
    })),
    combine: (results) => ({
      data: { results: results.map((result) => result.data?.results) },
      isPending: results.some((result) => result.isPending),
    }),
  });
  const { data, isPending: legacyIsPending } = useAppsByIds(appIds, enabled);

  const providerApps = useMemo(
    () =>
      (tivoApps.data?.results || [])
        .flatMap((res) => res || [])
        .map(providerToApp),
    [tivoApps.data]
  );

  const legacyApps = useMemo(
    () => (data?.results || []).map(legacyAppToListItem),
    [data]
  );

  const isPending = Boolean(legacyIsPending || tivoApps.isPending);

  return {
    isPending,
    apps: [...providerApps, ...legacyApps],
  };
}
